import React from 'react';
import { countryCatalogByIsoCode, CountrySelect } from '../../../../shared/ui/CountrySelect';
import iconChevronDown from '../../assets/icon-chevron-down.svg';
import {
  AUTH_MODAL_COLOR,
  AUTH_MODAL_COPY,
  authModalMetrics,
  authModalType,
  AuthModalLayout,
} from './authModal.constants';

type AuthCountrySelectProps = {
  layout: AuthModalLayout;
  /** ISO code, e.g. "IN". */
  value: string;
  /** Without it the picker is read only. */
  onChange?: (countryCode: string) => void;
  disabled?: boolean;
};

/**
 * Country dial-code picker styled as the grey pill beside the mobile number field:
 * flag, dial code, chevron. The pill hugs the dial code and keeps that width while
 * open; a typed search scrolls inside it.
 */
function AuthCountrySelect({ layout, value, onChange, disabled }: AuthCountrySelectProps) {
  const metrics = authModalMetrics(layout);
  const type = authModalType(layout);
  const phoneCode = countryCatalogByIsoCode[value]?.phoneCode ?? '';
  return (
    <CountrySelect
      value={value}
      label=""
      ariaLabel={AUTH_MODAL_COPY.countryLabel}
      readOnly={!onChange}
      disabled={disabled}
      onChange={(countryCode) => {
        if (countryCode && onChange) onChange(countryCode);
      }}
      popupIcon={<img src={iconChevronDown} alt="" />}
      sx={{
        flexShrink: 0,
        // Doubled to outrank Autocomplete's padding reserved for its absolutely placed icon.
        '&& .MuiOutlinedInput-root': {
          flexWrap: 'nowrap',
          height: metrics.fieldHeight,
          px: `${metrics.dividerGap}px`,
          py: 0,
          borderRadius: `${metrics.fieldGap}px`,
          backgroundColor: AUTH_MODAL_COLOR.country,
          cursor: onChange ? 'pointer' : 'default',
          '& fieldset': { border: 0 },
        },
        '&& .MuiAutocomplete-input': {
          ...type.code,
          color: AUTH_MODAL_COLOR.text,
          p: 0,
          ml: `${metrics.fieldGap}px`,
          minWidth: 0,
          flexGrow: 0,
          width: `${phoneCode.length}ch`,
          cursor: 'inherit',
        },
        '& .MuiAutocomplete-endAdornment': {
          position: 'static',
          transform: 'none',
          display: 'flex',
          ml: `${metrics.inputGap}px`,
        },
        '& .MuiAutocomplete-popupIndicator': { p: 0, m: 0 },
        '& .MuiInputAdornment-root, & [data-testid="country-flag"]': { ml: 0 },
      }}
    />
  );
}

export default AuthCountrySelect;
